import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import CTABand from "@/components/CTABand";
import { SERVICES, PROCESS_STEPS } from "@/data/site";

export default function ServiceDetail() {
  const { slug } = useParams();
  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const service = SERVICES[idx];

  useEffect(() => {
    if (service) {
      document.title = `${service.title} — LEDOS Enterprise Solutions`;
    }
  }, [service]);

  if (!service) return <Navigate to="/services" replace />;

  const prev = SERVICES[(idx - 1 + SERVICES.length) % SERVICES.length];
  const next = SERVICES[(idx + 1) % SERVICES.length];

  return (
    <main data-testid={`service-detail-${service.slug}`}>
      <section className="relative bg-ink pt-44 pb-20 md:pb-28 overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 grid-bg" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <MaskedLine delay={0.1}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-signal mb-8">
              Service {service.num} / 08
            </p>
          </MaskedLine>
          <h1 className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-6xl lg:text-7xl max-w-5xl">
            <MaskedLine delay={0.25}>{service.title}</MaskedLine>
          </h1>
          <Reveal delay={0.45} className="mt-10 max-w-2xl">
            <p className="text-base md:text-lg leading-relaxed text-zinc-400">{service.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="group">
            <div className="clip-frame relative">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-[45vh] md:h-[60vh] object-cover img-field"
              />
              <div className="absolute inset-0 bg-black/30" />
            </div>
            <div className="flex justify-between gap-4 mt-3 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">
              <span>
                FIG. {service.num} — {service.title}
              </span>
              <span className="text-signal shrink-0">Illustrative image</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-panel border-y border-line py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16">
          <div>
            <SectionHeading index="S." eyebrow="Scope" title="What This Covers" />
            <Reveal delay={0.1}>
              <p className="text-base leading-relaxed text-zinc-400">{service.body}</p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <ul className="border-t border-line">
              {service.capabilities.map((cap) => (
                <li
                  key={cap}
                  data-testid={`capability-item-${cap.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="flex items-center gap-4 py-4 border-b border-line text-sm md:text-base text-zinc-300"
                >
                  <Check className="w-4 h-4 text-signal shrink-0" />
                  {cap}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading index="P." eyebrow="Delivery" title="How the Work Gets Done" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-line border border-line">
            {PROCESS_STEPS.map((step, i) => (
              <Reveal key={step.num} delay={i * 0.05} className="bg-ink">
                <div className="p-6 h-full hover:bg-panel transition-colors duration-500">
                  <span className="font-display text-3xl font-black text-outline-signal block mb-4">
                    {step.num}
                  </span>
                  <p className="font-display text-lg font-bold tracking-tight text-white mb-2">
                    {step.title}
                  </p>
                  <p className="text-xs leading-relaxed text-zinc-500">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="service-detail-cta"
              className="group mt-14 inline-flex items-center gap-2 bg-signal text-black font-mono text-xs font-bold tracking-[0.15em] uppercase px-8 py-5 hover:-translate-y-1 transition-transform duration-300"
            >
              Discuss Your Project
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <nav
        aria-label="More services"
        className="border-t border-line bg-panel grid md:grid-cols-2"
      >
        <Link
          to={`/services/${prev.slug}`}
          data-testid="service-prev-link"
          className="group flex items-center gap-4 p-8 md:p-12 border-b md:border-b-0 md:border-r border-line hover:bg-surface transition-colors duration-500"
        >
          <ArrowLeft className="w-5 h-5 text-zinc-500 group-hover:text-signal group-hover:-translate-x-1 transition-all duration-300" />
          <span>
            <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-600 mb-1">
              Previous
            </span>
            <span className="font-display text-lg md:text-xl font-bold tracking-tight text-white group-hover:text-signal transition-colors duration-300">
              {prev.title}
            </span>
          </span>
        </Link>
        <Link
          to={`/services/${next.slug}`}
          data-testid="service-next-link"
          className="group flex items-center justify-end gap-4 p-8 md:p-12 text-right hover:bg-surface transition-colors duration-500"
        >
          <span>
            <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-600 mb-1">
              Next
            </span>
            <span className="font-display text-lg md:text-xl font-bold tracking-tight text-white group-hover:text-signal transition-colors duration-300">
              {next.title}
            </span>
          </span>
          <ArrowRight className="w-5 h-5 text-zinc-500 group-hover:text-signal group-hover:translate-x-1 transition-all duration-300" />
        </Link>
      </nav>

      <CTABand />
    </main>
  );
}
